import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useCarrinhoStore } from '@/stores/carrinhoStore';
import { useAuthStore } from '@/stores/authStore';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import api from '@/lib/api';
import { formatCurrency, formatCPF, formatPhone } from '@/lib/utils';
import { 
  CreditCard, 
  QrCode, 
  FileText, 
  Truck, 
  Check, 
  ChevronRight, 
  ChevronLeft,
  Copy,
  Package,
  MapPin,
  User,
  Lock
} from 'lucide-react';

const steps = [
  { id: 1, title: 'Dados Pessoais', icon: User },
  { id: 2, title: 'Entrega', icon: Truck },
  { id: 3, title: 'Pagamento', icon: Lock },
];

const metodosPagamento = [
  { 
    id: 'PIX', 
    label: 'PIX', 
    icon: QrCode, 
    desc: 'Pagamento instantâneo com 5% de desconto',
    desconto: 5 
  },
  { 
    id: 'CARTAO', 
    label: 'Cartão de Crédito', 
    icon: CreditCard, 
    desc: 'Até 12x sem juros',
    desconto: 0 
  },
  { 
    id: 'BOLETO', 
    label: 'Boleto Bancário', 
    icon: FileText, 
    desc: 'Vencimento em 3 dias úteis',
    desconto: 0 
  },
];

export function Checkout() {
  const navigate = useNavigate();
  const { items, subtotal, desconto, cupomCodigo, limparCarrinho } = useCarrinhoStore();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [pedidoCriado, setPedidoCriado] = useState<any>(null);
  const [pagamentoData, setPagamentoData] = useState<any>(null);
  
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    cpf: '',
    cep: '',
    endereco: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    estado: '',
  });
  
  const [frete, setFrete] = useState({
    tipo: 'CORREIOS_PAC',
    valor: 15.90,
    prazo: '5-7 dias úteis',
  });
  
  const [metodoPagamento, setMetodoPagamento] = useState('PIX');
  const [parcelas, setParcelas] = useState(1);
  const [cartaoData, setCartaoData] = useState({
    numero: '',
    titular: '',
    validade: '',
    cvv: '',
  });

  const freteOptions = [
    { tipo: 'CORREIOS_PAC', label: 'Correios PAC', valor: 15.90, prazo: '5-7 dias úteis' },
    { tipo: 'CORREIOS_SEDEX', label: 'Correios SEDEX', valor: 25.90, prazo: '2-3 dias úteis' },
    { tipo: 'MOTOBOY', label: 'Motoboy (SP Capital)', valor: 19.90, prazo: '1-2 dias úteis' },
    { tipo: 'RETIRADA', label: 'Retirada na Loja', valor: 0, prazo: 'Imediato' },
  ];

  const isFreteGratis = subtotal() >= 250;

  const [clienteLevel, setClienteLevel] = useState<string>('BRONZE');
  const { user, tipo, isAuthenticated } = useAuthStore();

  useEffect(() => {
    if (isAuthenticated && tipo === 'cliente' && user?.id) {
      api.get('/clientes/me').then(res => {
        if (res.data.fidelidade?.nivel) {
          setClienteLevel(res.data.fidelidade.nivel);
        }
      });
    }
  }, [isAuthenticated, tipo, user?.id]);

  const isVIP = clienteLevel === 'DIAMANTE' || clienteLevel === 'OURO';
  const vipDescontoPercent = isVIP ? 10 : 0;

  const calcularTotal = () => {
    const metodo = metodosPagamento.find(m => m.id === metodoPagamento);
    const descontoPagamento = metodo?.desconto || 0;
    const valorDescontoPagamento = (subtotal() * (descontoPagamento + vipDescontoPercent)) / 100;
    const valorFrete = isFreteGratis ? 0 : frete.valor;
    return subtotal() - desconto - valorDescontoPagamento + valorFrete;
  };

  const handleCepChange = async (cep: string) => {
    const cepLimpo = cep.replace(/\D/g, '');
    setFormData({ ...formData, cep });
    
    if (cepLimpo.length === 8) {
      try {
        const response = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`);
        const data = await response.json();
        if (!data.erro) {
          setFormData(prev => ({
            ...prev,
            endereco: data.logradouro,
            bairro: data.bairro,
            cidade: data.localidade,
            estado: data.uf,
          }));
        }
      } catch (error) {
        console.error('Erro ao buscar CEP:', error);
      }
    }
  };

  const criarPedido = async () => {
    setLoading(true);
    try {
      // Criar cliente
      const clienteResponse = await api.post('/clientes', {
        nome: formData.nome,
        email: formData.email,
        telefone: formData.telefone,
        cpfCnpj: formData.cpf.replace(/\D/g, ''),
        endereco: `${formData.endereco}, ${formData.numero}${formData.complemento ? ` - ${formData.complemento}` : ''}`,
        cidade: formData.cidade,
        estado: formData.estado,
        cep: formData.cep.replace(/\D/g, ''),
      });

      const clienteId = clienteResponse.data.id;

      // Criar pedido
      const pedidoResponse = await api.post('/pedidos', {
        origem: 'SITE',
        clienteId,
        tipoPagamento: metodoPagamento === 'CARTAO' && parcelas > 1 ? 'PARCELADO_50_50' : 'AVISTA',
        items: items.map(item => ({
          produtoId: item.produto.id,
          quantidade: item.quantidade,
          precoUnitario: item.produto.precoVenda,
          custoUnitarioReal: item.produto.custoRealTotal,
        })),
        freteValor: isFreteGratis ? 0 : frete.valor,
        freteModal: frete.tipo.split('_')[0],
        cupomCodigo: cupomCodigo || undefined,
      });

      setPedidoCriado(pedidoResponse.data);
      return pedidoResponse.data;
    } catch (error) {
      console.error('Erro ao criar pedido:', error);
      throw error;
    }
  };

  const criarPagamento = async (pedido: any) => {
    try {
      const response = await api.post('/pagamentos', {
        pedidoId: pedido.id,
        metodoPagamento,
        parcelas: metodoPagamento === 'CARTAO' ? parcelas : 1,
      });

      setPagamentoData(response.data);
      return response.data;
    } catch (error) {
      console.error('Erro ao criar pagamento:', error);
      throw error;
    }
  };

  const finalizarCompra = async () => {
    setLoading(true);
    try {
      const pedido = await criarPedido();
      const pagamento = await criarPagamento(pedido);
      
      if (metodoPagamento === 'CARTAO') {
        // Processar pagamento com cartão
        await api.post(`/pagamentos/${pagamento.pagamento.id}/cartao`, {
          creditCard: {
            holderName: cartaoData.titular,
            number: cartaoData.numero.replace(/\s/g, ''),
            expiryMonth: cartaoData.validade.split('/')[0],
            expiryYear: `20${cartaoData.validade.split('/')[1]}`,
            ccv: cartaoData.cvv,
          },
          creditCardHolderInfo: {
            name: formData.nome,
            email: formData.email,
            cpfCnpj: formData.cpf.replace(/\D/g, ''),
            postalCode: formData.cep.replace(/\D/g, ''),
            addressNumber: formData.numero,
            addressComplement: formData.complemento,
            phone: formData.telefone.replace(/\D/g, ''),
          },
          parcelas,
        });
      }
      
      setCurrentStep(4);
      limparCarrinho();
    } catch (error) {
      console.error('Erro ao finalizar compra:', error);
      alert('Erro ao processar pagamento. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  if (items.length === 0 && currentStep !== 4) {
    navigate('/carrinho');
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-950 py-12">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-bold text-white mb-2">Finalizar Compra</h1>
          <p className="text-gray-400">Complete suas informações para finalizar o pedido</p>
        </motion.div>

        {/* Steps */}
        <div className="flex items-center justify-center mb-8">
          {steps.map((step, index) => (
            <div key={step.id} className="flex items-center">
              <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${
                currentStep >= step.id 
                  ? 'bg-gradient-to-r from-orange-500 to-pink-500 text-white' 
                  : 'bg-gray-800 text-gray-500'
              }`}>
                <step.icon className="w-4 h-4" />
                <span className="text-sm font-medium hidden sm:block">{step.title}</span>
              </div>
              {index < steps.length - 1 && (
                <ChevronRight className="w-5 h-5 text-gray-600 mx-2" />
              )}
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <AnimatePresence mode="wait">
              {/* Step 1: Dados Pessoais */}
              {currentStep === 1 && (
                <motion.div
                  key="step1"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-6 border border-white/5"
                >
                  <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                    <User className="w-5 h-5 text-orange-400" />
                    Dados Pessoais
                  </h2>
                  
                  <div className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <Input
                        label="Nome Completo *"
                        value={formData.nome}
                        onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                        placeholder="João Silva"
                        required
                      />
                      <Input
                        label="CPF *"
                        value={formData.cpf}
                        onChange={(e) => setFormData({ ...formData, cpf: formatCPF(e.target.value) })}
                        placeholder="000.000.000-00"
                        maxLength={14}
                        required
                      />
                    </div>
                    
                    <div className="grid sm:grid-cols-2 gap-4">
                      <Input
                        label="E-mail *"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="joao@email.com"

                        required
                      />
                      <Input
                        label="Telefone *"
                        value={formData.telefone}
                        onChange={(e) => setFormData({ ...formData, telefone: formatPhone(e.target.value) })}
                        placeholder="(11) 99999-9999"

                        maxLength={15}
                        required
                      />
                    </div>
                  </div>

                  <div className="mt-6 flex justify-end">
                    <Button
                      variant="nuvem"
                      onClick={() => setCurrentStep(2)}
                      disabled={!formData.nome || !formData.email || !formData.cpf || !formData.telefone}
                      rightIcon={<ChevronRight className="w-4 h-4" />}
                    >
                      Continuar
                    </Button>
                  </div>
                </motion.div>
              )}

              {/* Step 2: Entrega */}
              {currentStep === 2 && (
                <motion.div
                  key="step2"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-6"
                >
                  {/* Endereço */}
                  <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-6 border border-white/5">
                    <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-orange-400" />
                      Endereço de Entrega
                    </h2>
                    
                    <div className="space-y-4">
                      <div className="grid sm:grid-cols-3 gap-4">
                        <div className="sm:col-span-1">
                          <Input
                            label="CEP *"
                            value={formData.cep}
                            onChange={(e) => handleCepChange(e.target.value)}
                            placeholder="00000-000"
                            maxLength={9}
                            required
                          />
                        </div>
                      </div>
                      
                      <div className="grid sm:grid-cols-3 gap-4">
                        <div className="sm:col-span-2">
                          <Input
                            label="Endereço *"
                            value={formData.endereco}
                            onChange={(e) => setFormData({ ...formData, endereco: e.target.value })}
                            placeholder="Rua, Avenida, etc"
                            required
                          />
                        </div>
                        <Input
                          label="Número *"
                          value={formData.numero}
                          onChange={(e) => setFormData({ ...formData, numero: e.target.value })}
                          placeholder="123"
                          required
                        />
                      </div>
                      
                      <div className="grid sm:grid-cols-2 gap-4">
                        <Input
                          label="Complemento"
                          value={formData.complemento}
                          onChange={(e) => setFormData({ ...formData, complemento: e.target.value })}
                          placeholder="Apto, Bloco, etc"
                        />
                        <Input
                          label="Bairro *"
                          value={formData.bairro}
                          onChange={(e) => setFormData({ ...formData, bairro: e.target.value })}
                          placeholder="Bairro"
                          required
                        />
                      </div>
                      
                      <div className="grid sm:grid-cols-2 gap-4">
                        <Input
                          label="Cidade *"
                          value={formData.cidade}
                          onChange={(e) => setFormData({ ...formData, cidade: e.target.value })}
                          placeholder="Cidade"
                          required
                        />
                        <Input
                          label="Estado *"
                          value={formData.estado}
                          onChange={(e) => setFormData({ ...formData, estado: e.target.value })}
                          placeholder="UF"
                          maxLength={2}
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* Frete */}
                  <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-6 border border-white/5">
                    <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                      <Truck className="w-5 h-5 text-orange-400" />
                      Opções de Frete
                    </h2>
                    
                    <div className="space-y-3">
                      {freteOptions.map((option) => (
                        <label
                          key={option.tipo}
                          className={`flex items-center justify-between p-4 rounded-xl border-2 cursor-pointer transition-all ${
                            frete.tipo === option.tipo
                              ? 'border-orange-500 bg-orange-500/10'
                              : 'border-white/10 hover:border-white/20 bg-white/5'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <input
                              type="radio"
                              name="frete"
                              value={option.tipo}
                              checked={frete.tipo === option.tipo}
                              onChange={() => setFrete(option)}
                              className="w-4 h-4 accent-orange-500"
                            />
                            <div>
                              <p className="text-white font-medium">{option.label}</p>
                              <p className="text-gray-400 text-sm">{option.prazo}</p>
                            </div>
                          </div>
                          <span className={`${isFreteGratis ? 'text-green-400 line-through text-xs mr-2' : 'text-white'} font-bold`}>
                            {option.valor === 0 ? 'Grátis' : formatCurrency(option.valor)}
                          </span>
                          {isFreteGratis && option.valor > 0 && (
                            <span className="text-white font-bold">Grátis</span>
                          )}
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="flex justify-between">
                    <Button
                      variant="outline"
                      onClick={() => setCurrentStep(1)}
                      leftIcon={<ChevronLeft className="w-4 h-4" />}
                    >
                      Voltar
                    </Button>
                    <Button
                      variant="nuvem"
                      onClick={() => setCurrentStep(3)}
                      disabled={!formData.cep || !formData.endereco || !formData.numero}
                      rightIcon={<ChevronRight className="w-4 h-4" />}
                    >
                      Continuar
                    </Button>
                  </div>
                </motion.div>
              )}

              {/* Step 3: Pagamento */}
              {currentStep === 3 && (
                <motion.div
                  key="step3"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-6"
                >
                  {/* Método de Pagamento */}
                  <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-6 border border-white/5">
                    <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                      <Lock className="w-5 h-5 text-orange-400" />
                      Forma de Pagamento
                    </h2>
                    
                    <div className="space-y-3 mb-6">
                      {metodosPagamento.map((metodo) => (
                        <label
                          key={metodo.id}
                          className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                            metodoPagamento === metodo.id
                              ? 'border-orange-500 bg-orange-500/10'
                              : 'border-white/10 hover:border-white/20 bg-white/5'
                          }`}
                        >
                          <input
                            type="radio"
                            name="pagamento"
                            value={metodo.id}
                            checked={metodoPagamento === metodo.id}
                            onChange={() => setMetodoPagamento(metodo.id)}
                            className="w-4 h-4 accent-orange-500"
                          />
                          <metodo.icon className={`w-6 h-6 ${
                            metodoPagamento === metodo.id ? 'text-orange-400' : 'text-gray-400'
                          }`} />
                          <div className="flex-1">
                            <p className="text-white font-medium">{metodo.label}</p>
                            <p className="text-gray-400 text-sm">{metodo.desc}</p>
                          </div>
                          {metodo.desconto > 0 && (
                            <span className="px-3 py-1 rounded-full bg-green-500/20 text-green-400 text-sm font-medium">
                              -{metodo.desconto}%
                            </span>
                          )}
                        </label>
                      ))}
                    </div>

                    {/* Cartão Details */}
                    {metodoPagamento === 'CARTAO' && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        className="space-y-4 border-t border-white/10 pt-6"
                      >
                        <div className="grid sm:grid-cols-2 gap-4">
                          <div className="sm:col-span-2">
                            <Input
                              label="Número do Cartão *"
                              value={cartaoData.numero}
                              onChange={(e) => setCartaoData({ ...cartaoData, numero: e.target.value })}
                              placeholder="0000 0000 0000 0000"
                              maxLength={19}
                            />
                          </div>
                          <div className="sm:col-span-2">
                            <Input
                              label="Nome no Cartão *"
                              value={cartaoData.titular}
                              onChange={(e) => setCartaoData({ ...cartaoData, titular: e.target.value })}
                              placeholder="Como aparece no cartão"
                            />
                          </div>
                          <Input
                            label="Validade *"
                            value={cartaoData.validade}
                            onChange={(e) => setCartaoData({ ...cartaoData, validade: e.target.value })}
                            placeholder="MM/AA"
                            maxLength={5}
                          />
                          <Input
                            label="CVV *"
                            value={cartaoData.cvv}
                            onChange={(e) => setCartaoData({ ...cartaoData, cvv: e.target.value })}
                            placeholder="123"
                            maxLength={4}
                            type="password"
                          />
                        </div>

                        {/* Parcelas */}
                        <div>
                          <label className="block text-sm font-medium text-white mb-2">
                            Parcelas
                          </label>
                          <select
                            value={parcelas}
                            onChange={(e) => setParcelas(Number(e.target.value))}
                            className="w-full h-12 px-4 rounded-xl bg-gray-800 border border-white/10 text-white focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none"
                          >
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((p) => (
                              <option key={p} value={p}>
                                {p}x de {formatCurrency(calcularTotal() / p)} {p === 1 ? 'à vista' : 'sem juros'}
                              </option>
                            ))}
                          </select>
                        </div>
                      </motion.div>
                    )}
                  </div>

                  <div className="flex justify-between">
                    <Button
                      variant="outline"
                      onClick={() => setCurrentStep(2)}
                      leftIcon={<ChevronLeft className="w-4 h-4" />}
                    >
                      Voltar
                    </Button>
                    <Button
                      variant="nuvem"
                      onClick={finalizarCompra}
                      isLoading={loading}
                      disabled={metodoPagamento === 'CARTAO' && (!cartaoData.numero || !cartaoData.titular || !cartaoData.validade || !cartaoData.cvv)}
                      className="bg-gradient-to-r from-green-500 to-emerald-600"
                    >
                      {loading ? 'Processando...' : `Pagar ${formatCurrency(calcularTotal())}`}
                    </Button>
                  </div>
                </motion.div>
              )}

              {/* Step 4: Sucesso */}
              {currentStep === 4 && pagamentoData && (
                <motion.div
                  key="step4"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-8 border border-white/5 text-center"
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: "spring" }}
                    className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center"
                  >
                    <Check className="w-10 h-10 text-white" />
                  </motion.div>

                  <h2 className="text-2xl font-bold text-white mb-2">
                    Pedido Realizado com Sucesso!
                  </h2>
                  <p className="text-gray-400 mb-6">
                    Seu pedido #{pedidoCriado?.numeroPedido} foi recebido e está sendo processado.
                  </p>

                  {/* PIX Payment Info */}
                  {metodoPagamento === 'PIX' && pagamentoData?.pix && (
                    <div className="bg-gray-800/50 rounded-xl p-6 mb-6">
                      <h3 className="text-lg font-semibold text-white mb-4">Pagamento via PIX</h3>
                      <div className="flex flex-col items-center">
                        <img
                          src={`data:image/png;base64,${pagamentoData.pix.encodedImage}`}
                          alt="QR Code PIX"
                          className="w-48 h-48 rounded-xl mb-4"
                        />
                        <p className="text-gray-400 text-sm mb-2">Ou copie o código PIX:</p>
                        <div className="flex items-center gap-2 w-full max-w-md">
                          <code className="flex-1 p-3 bg-gray-900 rounded-lg text-xs text-gray-300 break-all">
                            {pagamentoData.pix.payload}
                          </code>
                          <button
                            onClick={() => copyToClipboard(pagamentoData.pix.payload)}
                            className="p-3 rounded-lg bg-orange-500 text-white hover:bg-orange-600 transition-colors"
                          >
                            <Copy className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Boleto Payment Info */}
                  {metodoPagamento === 'BOLETO' && pagamentoData?.asaas?.invoiceUrl && (
                    <div className="bg-gray-800/50 rounded-xl p-6 mb-6">
                      <h3 className="text-lg font-semibold text-white mb-4">Boleto Bancário</h3>
                      <p className="text-gray-400 mb-4">
                        Clique no botão abaixo para visualizar e imprimir seu boleto:
                      </p>
                      <a
                        href={pagamentoData.asaas.invoiceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Button variant="nuvem" leftIcon={<FileText className="w-4 h-4" />}>
                          Visualizar Boleto
                        </Button>
                      </a>
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Button variant="outline" onClick={() => navigate('/')}>
                      Voltar à Loja
                    </Button>
                    <Button
                      variant="nuvem"
                      onClick={() => navigate(`/pedido/${pedidoCriado?.id}`)}
                    >
                      Acompanhar Pedido
                    </Button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Resumo do Pedido */}
          <div className="lg:col-span-1">
            <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-6 border border-white/5 sticky top-24">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Package className="w-5 h-5 text-orange-400" />
                Resumo do Pedido
              </h3>

              {/* Items */}
              <div className="space-y-3 mb-4 max-h-60 overflow-y-auto">
                {items.map((item) => (
                  <div key={item.produto.id} className="flex items-center gap-3">
                    <img
                      src={item.produto.fotos?.[0] || '/placeholder.png'}
                      alt={item.produto.nome}
                      className="w-12 h-12 rounded-lg object-cover"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm truncate">{item.produto.nome}</p>
                      <p className="text-gray-400 text-xs">{item.quantidade}x</p>
                    </div>
                    <p className="text-white font-medium text-sm">
                      {formatCurrency(item.produto.precoVenda * item.quantidade)}
                    </p>
                  </div>
                ))}
              </div>

              <div className="border-t border-white/10 pt-4 space-y-2">
                <div className="flex justify-between text-gray-400">
                  <span>Subtotal</span>
                  <span>{formatCurrency(subtotal())}</span>
                </div>
                
                {desconto > 0 && (
                  <div className="flex justify-between text-green-400">
                    <span>Desconto Cupom</span>
                    <span>-{formatCurrency(desconto)}</span>
                  </div>
                )}
                
                {metodoPagamento === 'PIX' && (
                  <div className="flex justify-between text-green-400">
                    <span>Desconto PIX (5%)</span>
                    <span>-{formatCurrency((subtotal() * 5) / 100)}</span>
                  </div>
                )}
                
                <div className="flex justify-between text-gray-400">
                  <span>Frete ({freteOptions.find(f => f.tipo === frete.tipo)?.label})</span>
                  <span>{frete.valor === 0 ? 'Grátis' : formatCurrency(frete.valor)}</span>
                </div>

                <div className="border-t border-white/10 pt-2">
                  <div className="flex justify-between text-xl font-bold text-white">
                    <span>Total</span>
                    <span className="text-orange-400">{formatCurrency(calcularTotal())}</span>
                  </div>
                  {metodoPagamento === 'CARTAO' && parcelas > 1 && (
                    <p className="text-gray-400 text-sm text-right mt-1">
                      {parcelas}x de {formatCurrency(calcularTotal() / parcelas)} sem juros
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
